import {
	Tuple,
	Tuple2,
	Tuple3,
	Tuple4,
}	from "ts-base/tuple";
import { Fn, Fn2 }		from "ts-base/fn";
import { Endo }			from "ts-base/endo";
import { Is }			from "ts-base/is";
import { NotNull }		from "ts-base/notNull";
import { Predicate }	from "ts-base/predicate";
import { Maps }			from "ts-base/maps";
import { Equal }		from "ts-base/equal";
import { Monoid }		from "ts-base/monoid";
import { Comparator }	from "ts-base/comparator";
import { Nullable }		from "ts-base/nullable";
import { Nea }			from "ts-base/nea";
import { Maybe }		from "ts-base/maybe";
import { Either }		from "ts-base/either";
import { Validated }	from "ts-base/validated";
import { Strings }		from "ts-base/strings";
import { Numbers }		from "ts-base/numbers";
import { BigInts }		from "ts-base/bigints";
import { Booleans }		from "ts-base/booleans";
import { Dates }		from "ts-base/dates";
import { Sync }			from "ts-base/sync";
import { Async }		from "ts-base/async";
import { Zio }			from "ts-base/zio";

export namespace Arrays {
	//-----------------------------------------------------------------------------
	//## types

	export type Value<T>			= T extends Array<infer X>			? X : never;
	export type ReadonlyValue<T>	= T extends ReadonlyArray<infer X>	? X : never;

	//-----------------------------------------------------------------------------
	//## type helpers

	/** an identity function, but takes mutability away */
	export const readonly	= <T>(self:Array<T>):ReadonlyArray<T>	=> self;

	export const readonlyN	= <T>(...self:Array<T>):ReadonlyArray<T>	=> self;

	//-----------------------------------------------------------------------------
	//## construction

	export const empty	= <T>():Array<T> => [];

	export const single	= <T>(item:T):Array<T> => [ item ];

	export const zeroUntil	= (length:number):Array<number>	=>
		ofLength(length)(Fn.identity);

	export const inclusiveRange = (start:number, end:number):Array<number> =>
		end >= start
		?	ofLength(end - start + 1)(it => start + it)
		:	ofLength(start - end + 1)(it => start - it);

	export const replicate	= (length:number) => <T>(item:T):Array<T>	=>
		ofLength(length)(Fn.constant(item));

	export const ofLength	= (length:number) => <T>(func:Fn<number, T>):Array<T>	=>
		Array.from({ length }, (_, idx) => func(idx));

	export const fromNullable = <T>(item:T|null):Array<T> =>
		item !== null ? pure(item) : empty();

	export const when	= <T>(value:T) => (condition:boolean):Array<T>	=>
		condition ? pure(value) : empty();

	export const test	= <T>(value:T) => (pred:Predicate<T>):Array<T>	=>
		pred(value) ? pure(value) : empty();

	export const testIs	= <T>(value:T) => <U extends T>(pred:Is<T, U>):Array<U>	=>
		pred(value) ? pure(value) : empty();

	// just for convenience...
	export const singleIf	= <T>(condition:boolean, value:T):Array<T>	=>
		when(value)(condition);

	//-----------------------------------------------------------------------------
	//## type guards

	export const isItemOf	= <T>(self:ReadonlyArray<T>):Is<unknown, T>	=> (value:unknown):value is T	=>
		self.includes(value as T);

	//-----------------------------------------------------------------------------
	//## test

	export const includes	= <T>(self:ReadonlyArray<T>) => (value:T):boolean	=>
		self.includes(value);

	export const count	= <T>(self:ReadonlyArray<T>) => (pred:Predicate<T>):number	=> {
		let result	= 0;
		for (const item of self) {
			if (pred(item)) result += 1;
		}
		return result;
	};

	export const forall = <T>(self:ReadonlyArray<T>) => (pred:Predicate<T>):boolean	=>
		self.every(pred);

	export const exists = <T>(self:ReadonlyArray<T>) => (pred:Predicate<T>):boolean	=>
		self.some(pred);

	export const some	= exists;
	export const every	= forall;

	//-----------------------------------------------------------------------------
	//## index of

	export const firstIndexOfOrNull	= <T>(self:ReadonlyArray<T>) => (search:T, start?:number):number|null	=>
		indexOrNull(self.indexOf(search, start));

	export const lastIndexOfOrNull	= <T>(self:ReadonlyArray<T>) => (search:T, start?:number):number|null	=>
		indexOrNull(self.lastIndexOf(search, start));

	export const firstIndexOf	= <T>(self:ReadonlyArray<T>) => (search:T, start?:number):Maybe<number>	=>
		Maybe.fromNullable(firstIndexOfOrNull(self)(search, start));

	export const lastIndexOf	= <T>(self:ReadonlyArray<T>) => (search:T, start?:number):Maybe<number>	=>
		Maybe.fromNullable(lastIndexOfOrNull(self)(search, start));

	const indexOrNull	= (index:number):number|null	=>
		index !== -1 ? index : null;

	//-----------------------------------------------------------------------------
	//## finding

	export const findFirstIndexOrNull		= <T>(self:ReadonlyArray<T>, start?:number) => (pred:Fn<T, boolean>):number|null	=> {
		const tot	= start ?? 0;
		for (let idx=tot; idx < self.length; idx++) {
			if (pred(self[idx]))	return idx;
		}
		return null;
	};

	export const findLastIndexOrNull	= <T>(self:ReadonlyArray<T>, start?:number) => (pred:Fn<T, boolean>):number|null	=> {
		const tot	= start ?? self.length-1;
		for (let idx=tot; idx >= 0; idx--) {
			if (pred(self[idx]))	return idx;
		}
		return null;
	};

	export const findFirstIndex		= <T>(self:ReadonlyArray<T>, start?:number) => (pred:Fn<T, boolean>):Maybe<number>	=>
		Maybe.fromNullable(findFirstIndexOrNull(self, start)(pred));

	export const findLastIndex	= <T>(self:ReadonlyArray<T>, start?:number) => (pred:Fn<T, boolean>):Maybe<number>	=>
		Maybe.fromNullable(findFirstIndexOrNull(self, start)(pred));

	export const find	= <T>(self:ReadonlyArray<T>) => (pred:Predicate<T>):Maybe<T>	=> {
		const item	= self.find(pred);
		return item === undefined ? Maybe.none() : Maybe.some(item);
	};

	export const findIndex	= <T>(self:ReadonlyArray<T>) => (pred:Predicate<T>):Maybe<number>	=> {
		const index	= self.findIndex(pred);
		return index === -1 ? Maybe.none() : Maybe.some(index);
	};

	//-----------------------------------------------------------------------------
	//## indexed

	export const at	= <T>(self:ReadonlyArray<T>) => (index:number):Maybe<T> =>
		Maybe.when(self[index])(index >= 0 && index < self.length);

	export type ZeroOneOrMoreVisitor<T, Z, O, M>	= {
		zero:	()				=> Z,
		one:	(it:T)			=> O,
		more:	(its:Nea<T>)	=> M,
	};

	export const zeroOneMore	= <T>(self:ReadonlyArray<T>) => <Z, O, M>(visitor:ZeroOneOrMoreVisitor<T, Z, O, M>):Z|O|M	=>
		self.length === 0 ? visitor.zero()			:
		self.length === 1 ? visitor.one(self[0])	:
		visitor.more(self as Nea<T>);

	export const only	= <T>(self:ReadonlyArray<T>):Maybe<T>	=>
		Maybe.when(self[0])(self.length === 1);

	export const head	= <T>(self:ReadonlyArray<T>):Maybe<T>	=>
		Maybe.when(self[0])(self.length !== 0);

	export const last	= <T>(self:ReadonlyArray<T>):Maybe<T>	=>
		Maybe.when(self[self.length-1])(self.length !== 0);

	export const onlyOrNull	= <T>(self:ReadonlyArray<T>):T|null	=>
		self.length === 1 ? self[0] : null;

	export const headOrNull	= <T>(self:ReadonlyArray<T>):T|null	=>
		self.length !== 0 ? self[0] : null;

	export const lastOrNull	= <T>(self:ReadonlyArray<T>):T|null	=>
		self.length !== 0 ? self[self.length-1] : null;

	export const unsafeOnly	= <T>(self:ReadonlyArray<T>):T	=> {
		if (self.length !== 1)	throw new Error(`expected exactly one element but got ${self.length}`);
		return self[0];
	};

	export const unsafeHead	= <T>(self:ReadonlyArray<T>):T	=> {
		if (self.length < 1)	throw new Error("expected at least one element");
		return self[0];
	};

	export const unsafeLast	= <T>(self:ReadonlyArray<T>):T	=> {
		if (self.length < 1)	throw new Error("expected at least one element");
		return self[self.length-1];
	};

	export const onlyOrThrow	= <T>(self:ReadonlyArray<T>, error:unknown):T	=> {
		if (self.length === 1)	return self[0];
		else					throw error;
	};

	export const headOrThrow	= <T>(self:ReadonlyArray<T>, error:unknown):T	=> {
		if (self.length !== 0)	return self[0];
		else					throw error;
	};

	export const lastOrThrow	= <T>(self:ReadonlyArray<T>, error:unknown):T	=> {
		if (self.length !== 0)	return self[self.length-1];
		else					throw error;
	};

	export const onlyOrAll	= <T>(self:ReadonlyArray<T>):Either<ReadonlyArray<T>, T>	=>
		self.length === 1 ? Either.right(self[0]) : Either.left(self);

	// return array without last item
	export const init = <T>(self:ReadonlyArray<T>):Array<T> =>
		self.slice(0, self.length - 1);

	// return array without first item
	export const tail = <T>(self:ReadonlyArray<T>):Array<T> =>
		self.slice(1, self.length);

	export const reverse	= <T>(self:ReadonlyArray<T>):Array<T>	=> {
		const out	= self.slice(0);
		out.reverse();
		return out;
	};

	export const takeLeft	= <T>(self:ReadonlyArray<T>)	=> (length:number):ReadonlyArray<T>	=>
		self.slice(0, length);

	export const takeRight	= <T>(self:ReadonlyArray<T>)	=> (length:number):ReadonlyArray<T>	=>
		self.slice(self.length-length, self.length);

	export const dropLeft	= <T>(self:ReadonlyArray<T>)	=> (length:number):ReadonlyArray<T>	=>
		self.slice(length, self.length);

	export const dropRight	= <T>(self:ReadonlyArray<T>)	=> (length:number):ReadonlyArray<T>	=>
		self.slice(0, self.length-length);

	//-----------------------------------------------------------------------------
	//## modification

	// appends item to the end of array and returns copy of array
	export const append = <T>(self:ReadonlyArray<T>) => (item:T):Array<T> =>
		self.concat([ item ]);

	// add given item at the beginning of array
	export const prepend = <T>(self:ReadonlyArray<T>) => (item:T):Array<T> =>
		[ item ].concat(self);

	export const concat = <T>(as:ReadonlyArray<T>, bs:ReadonlyArray<T>):Array<T> =>
		as.concat(bs);

	/*
	// appends items to the end of array and returns copy of array
	export const appendMany = <T>(self:ReadonlyArray<T>) => (items:ReadonlyArray<T>):Array<T> =>
		self.concat(items);

	// appends items to the start of array and returns copy of array
	export const prependMany = <T>(self:ReadonlyArray<T>) => (items:ReadonlyArray<T>):Array<T> =>
		items.concat(self);
	*/

	export const append_	= Fn.flip(append);
	export const prepend_	= Fn.flip(prepend);

	export const intercalate = <T>(self:ReadonlyArray<T>) => <U>(separator:U):Array<T|U> => {
		const out:Array<T|U> = [];
		for (let i=0; i < self.length; i++) {
			if (i !== 0) out.push(separator);
			out.push(self[i]);
		}
		return out;
	};

	/**
	* calculate items for gaps before between and after elements
	* always returns one element more than the input
	*/
	export const gapped	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn2<Maybe<S>, Maybe<S>, T>):Array<T>	=> {
		if (self.length === 0)	return [ func(Maybe.none(), Maybe.none()) ];

		const out	= [];
		const last	= self.length - 1;
		out.push(func(
			Maybe.none(),
			Maybe.some(self[0]))
		);
		for (let i=0; i<last; i++) {
			out.push(func(
				Maybe.some(self[i]),
				Maybe.some(self[i+1])
			));
		}
		out.push(func(
			Maybe.some(self[last]),
			Maybe.none()
		));
		return out;
	};

	export const padStart	= <T>(self:ReadonlyArray<T>) => (padding:T) => (width:number):Array<T>	=>
		self.length < width ? concat(replicate(width - self.length)(padding), self) : Array.from(self);

	export const padEnd	= <T>(self:ReadonlyArray<T>) => (padding:T) => (width:number):Array<T>	=>
		self.length < width ? concat(self, replicate(width - self.length)(padding)) : Array.from(self);

	//-----------------------------------------------------------------------------
	//## specials

	export const pluck	=	<S>(self:ReadonlyArray<S>) => <K extends keyof S>(key:K):Array<S[K]>	=>
		self.map(it => it[key]);

	//-----------------------------------------------------------------------------
	//## filtering

	export const filter = <T>(self:ReadonlyArray<T>) => (pred:Predicate<T>):Array<T> =>
		self.filter(pred);

	export const filterNot = <T>(self:ReadonlyArray<T>) => (pred:Predicate<T>):Array<T> =>
		self.filter(Predicate.not(pred));

	export const partition = <T>(self:ReadonlyArray<T>) => (pred:Predicate<T>):Tuple2<Array<T>, Array<T>> =>
		[
			self.filter(pred),
			self.filter(Predicate.not(pred)),
		];

	export const partitionValidated	= <E, T>(self:ReadonlyArray<Validated<E, T>>):Tuple2<Array<Nea<E>>, Array<T>>	=> {
		const errorses	= [];
		const values	= [];
		for (const item of self) {
			if (Validated.isValid(item))	values.push(item.value);
			else							errorses.push(item.errors);
		}
		return [ errorses, values ];
	};

	export const partitionEither	= <E, T>(self:ReadonlyArray<Either<E, T>>):Tuple2<Array<E>, Array<T>>	=> {
		const errorses	= [];
		const values	= [];
		for (const item of self) {
			if (Either.isRight(item))	values.push(item.value);
			else						errorses.push(item.error);
		}
		return [ errorses, values ];
	};

	export const filterIs	= <T>(self:ReadonlyArray<T>) => <U extends T>(is:Is<T, U>):Array<U>	=>
		self.filter(is);

	//-----------------------------------------------------------------------------
	//## collapse

	export const collapse = <T>(self:ReadonlyArray<Maybe<T>>):Array<T>	=>
		flatMap(self)(Maybe.toArray);

	export const collapseMap = <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Maybe<T>>):Array<T>	=>
		flatMap(self)(Fn.andThen(func, Maybe.toArray));

	export const collapseNullable	= <T>(self:ReadonlyArray<T|null>):Array<T>	=>
		filterIs(self)(NotNull.is);

	export const collapseMapNullable = <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, T|null>):Array<T>	=>
		collapseNullable(self.map(func));

	//-----------------------------------------------------------------------------
	//## collapse first

	export const collapseFirst	= <T>(self:ReadonlyArray<Maybe<T>>):Maybe<T>	=> {
		for (const item of self) {
			if (Maybe.isSome(item))	return item;
		}
		return Maybe.none();
	};

	export const collapseMapFirst	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Maybe<T>>):Maybe<T>	=> {
		for (const item of self) {
			const out	= func(item);
			if (Maybe.isSome(out))	return out;
		}
		return Maybe.none();
	};

	export const collapseFirstNullable	= <T>(self:ReadonlyArray<T|null>):T|null	=> {
		for (const item of self) {
			if (item !== null)	return item;
		}
		return null;
	};

	export const collapseMapFirstNullable	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, T|null>):T|null	=> {
		for (const item of self) {
			const out	= func(item);
			if (out !== null)	return out;
		}
		return null;
	};

	//-----------------------------------------------------------------------------
	//## functor

	export const map = <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, T>):Array<T> =>
		self.map(func);

	export const tupleBy	= <T>(self:ReadonlyArray<T>) => <U>(func:Fn<T, U>):Array<Tuple2<T, U>>	=>
		map(self)(it => [ it, func(it) ]);

	export const zipWithIndex	= <T>(self:ReadonlyArray<T>):Array<Tuple2<T, number>>	=>
		self.map((item, index) => [ item, index ]);

	//-----------------------------------------------------------------------------
	//## applicative functor

	export const pure	= single;

	export const ap = <S>(self:ReadonlyArray<S>) => <T>(funcs:ReadonlyArray<Fn<S, T>>):Array<T> =>
		flatMap(funcs)(map(self));

	export const map2	= <S1, S2>(s1:ReadonlyArray<S1>, s2:ReadonlyArray<S2>) => <T>(func:Fn2<S1, S2, T>):Array<T>	=>
		flatMap(s1)((s1) =>
		map(s2)((s2) =>
			func(s1, s2)
		));

	export const tuple2	= <T1, T2>(t1:ReadonlyArray<T1>, t2:ReadonlyArray<T2>):Array<Tuple2<T1, T2>>	=>
		map2(t1, t2)(Tuple.ofN);

	export const tupleN	= <T1, T extends ReadonlyArray<ReadonlyArray<unknown>>>(arg:ReadonlyArray<T1>, ...args:T):ReadonlyArray<Tuple.Prepend<T1, ValueN<T>>>	=>
		mapN(arg, ...args)(Tuple.ofN);

	export const mapN	= <S1, S extends ReadonlyArray<ReadonlyArray<unknown>>>(arg:ReadonlyArray<S1>, ...args:S) => <T>(func:(arg:S1, ...args:ValueN<S>)=>T):ReadonlyArray<T>	=> {
		if (args.length === 0)	{
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			return arg.map(func as any);
		}
		else {
			return arg.flatMap(s1 => {
				const restFunc	= (...restArg:ValueN<S>):T	=> func(s1, ...restArg);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				return mapN(...(args as unknown as readonly[Array<unknown>]))(restFunc as any);
			});
		}
	};

	type ValueN<T>	= { [P in keyof T]:ReadonlyValue<T[P]> };

	//-----------------------------------------------------------------------------
	//## monad

	export const flatMap = <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, ReadonlyArray<T>>):Array<T> =>
		flatten(self.map(func));

	export const flatten = <T>(self:ReadonlyArray<ReadonlyArray<T>>):Array<T> =>
		Array.prototype.concat.apply([], self as Array<Array<T>>);
		// arr.reduce((a, b) => a.concat(b), []);

	export const andThen	= <A, B, C>(ab:Fn<A, ReadonlyArray<B>>, bc:Fn<B, ReadonlyArray<C>>):Fn<A, Array<C>>	=>
		a => flatMap(ab(a))(bc);

	export const andThen3	= <A, B, C, D>(ab:Fn<A, ReadonlyArray<B>>, bc:Fn<B, ReadonlyArray<C>>, cd:Fn<C, ReadonlyArray<D>>):Fn<A, Array<D>>	=>
		andThen(ab, andThen(bc, cd));

	export const andThen4	= <A, B, C, D, E>(ab:Fn<A, ReadonlyArray<B>>, bc:Fn<B, ReadonlyArray<C>>, cd:Fn<C, ReadonlyArray<D>>, de:Fn<D, ReadonlyArray<E>>):Fn<A, Array<E>>	=>
		andThen(ab, andThen(bc, andThen(cd, de)));

	//-----------------------------------------------------------------------------
	//## zippy applicative functor

	export const zipAp	= <S>(self:ReadonlyArray<S>) => <T>(func:ReadonlyArray<Fn<S, T>>):Array<T>	=>
		zipMap2(func, self)((f, v) => f(v));

	export const zipMap2 = <S1, S2>(s1:ReadonlyArray<S1>, s2:ReadonlyArray<S2>) => <T>(func:Fn2<S1, S2, T>):Array<T> => {
		const out:Array<T> = [];
		for (let i=0; i < s1.length && i < s2.length; i++) {
			out.push(func(s1[i], s2[i]));
		}
		return out;
	};

	export const zipTuple2 = <T1, T2>(t1:ReadonlyArray<T1>, t2:ReadonlyArray<T2>):Array<Tuple2<T1, T2>> =>
		zipMap2(t1, t2)(Tuple.ofN);


	export const zip = zipTuple2;

	export const zipN	= <T1, T extends ReadonlyArray<ReadonlyArray<unknown>>>(arg:ReadonlyArray<T1>, ...args:T):ReadonlyArray<Tuple.Prepend<T1, ValueN<T>>>	=>
		zipMapN(arg, ...args)(Tuple.ofN);

	export const zipMapN	= <S1, S extends ReadonlyArray<ReadonlyArray<unknown>>>(arg:ReadonlyArray<S1>, ...args:S) => <T>(func:(arg:S1, ...args:ValueN<S>)=>T):ReadonlyArray<T>	=> {
		if (args.length === 0)	return [];
		const out:Array<T>	= [];
		let rowIdx	= 0;
		for (;;) {
			if (rowIdx >= arg.length)	return out;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const tmp	= [] as any;
			for (const col of args) {
				if (rowIdx >= col.length)	return out;
				tmp.push(col[rowIdx]);
			}
			out.push(func(arg[rowIdx], ...tmp));
			rowIdx++;
		}
	};

	//-----------------------------------------------------------------------------
	//## unzip

	export const unzip = <T1, T2>(self:ReadonlyArray<Tuple2<T1, T2>>):Tuple2<Array<T1>, Array<T2>>	=>
		[
			self.map(([ t1, _t2 ]) => t1),
			self.map(([ _r1, t2 ]) => t2),
		];

	export const unzip3 = <T1, T2, T3>(self:ReadonlyArray<Tuple3<T1, T2, T3>>):Tuple3<Array<T1>, Array<T2>, Array<T3>>	=>
		[
			self.map(([ t1, _t2, _t3 ]) => t1),
			self.map(([ _t1, t2, _t3 ]) => t2),
			self.map(([ _t1, _t2, t3 ]) => t3),
		];

	export const unzip4 = <T1, T2, T3, T4>(self:ReadonlyArray<Tuple4<T1, T2, T3, T4>>):Tuple4<Array<T1>, Array<T2>, Array<T3>, Array<T4>>	=>
		[
			self.map(([ t1, _t2, _t3, _t4 ]) => t1),
			self.map(([ _t1, t2, _t3, _t4 ]) => t2),
			self.map(([ _t1, _t2, t3, _t4 ]) => t3),
			self.map(([ _t1, _t2, _t3, t4 ]) => t4),
		];

	// TODO nary add unzipN

	//-----------------------------------------------------------------------------
	//## equal

	export const equalWith	= <T>(a:ReadonlyArray<T>, b:ReadonlyArray<T>) => (equal:Equal<T>):boolean	=> {
		if (a === b)				return true;
		if (a.length !== b.length)	return false;
		for (let i=0; i<a.length; i++) {
			if (!equal(a[i], b[i]))	return false;
		}
		return true;
	};

	export const equalBuiltin	= <T>(a:ReadonlyArray<T>, b:ReadonlyArray<T>):boolean	=>
		equalWith(a, b)(Equal.builtin);

	/** remove duplicate items with a custom equality check */
	export const distinctWith = <T>(self:ReadonlyArray<T>) => (equal:Equal<T>):Array<T> => {
		const out:Array<T> = [];
		for (const item of self) {
			const exists = out.some(existing => equal(existing, item));
			if (!exists) out.push(item);
		}
		return out;
	};

	/** remove duplicate items with builtin equality */
	export const distinctBuiltin = <T>(self:ReadonlyArray<T>):Array<T> =>
		distinctWith(self)(Equal.builtin);

	/** remove duplicate Objects in Array where sameness is determined by comparing a single property of an individual object */
	export const distinctBuiltinByField = <T>(self:ReadonlyArray<T>) => <K extends keyof T>(key:K):Array<T> =>
		distinctWith(self)((existing, row) => existing[key] === row[key]);

	/** remove duplicate items with the SameValueZero algorithm used for equality of set items */
	export const distinctBuiltinFast	= <T>(self:ReadonlyArray<T>):Array<T>	=>
		Array.from(new Set(self));

	//-----------------------------------------------------------------------------
	//## monoid

	export const fold	= <T>(self:ReadonlyArray<T>) => (monoid:Monoid<T>):T	=>
		self.reduce(monoid.concat, monoid.empty);

	export const foldMap	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, T>) => (monoid:Monoid<T>):T	=>
		fold(self.map(func))(monoid);

	export const foldMapIntercalated	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, T>) => (separator:T) => (monoid:Monoid<T>):T	=>
		fold(intercalate(self.map(func))(separator))(monoid);

	//-----------------------------------------------------------------------------
	//## comparator

	/** sort arr by given comparison */
	export const sortWith = <T>(self:ReadonlyArray<T>) => (comparator:Comparator<T>):Array<T> => {
		const out = self.slice();
		out.sort(comparator);
		return out;
	};

	export const sortByWith	= <T>(self:ReadonlyArray<T>) => <U>(extract:Fn<T, U>) => (comparator:Comparator<U>):Array<T> =>
		sortWith(self)(Comparator.contraMap(comparator)(extract));

	/** sort array with natively comparable values */
	export const sortBuiltin	= <T>(self:ReadonlyArray<T>):Array<T>	=>
		sortWith(self)(Comparator.builtin);

	export const sortBuiltinBy	= <T>(self:ReadonlyArray<T>) => <U>(extract:Fn<T, U>):Array<T>	=>
		sortWith(self)(Comparator.builtinBy(extract));

	export const sortStrings = <T extends string>(self:ReadonlyArray<T>):Array<T> =>
		sortWith(self)(Strings.compare);

	export const sortNumbers = <T extends number>(self:ReadonlyArray<T>):Array<T> =>
		sortWith(self)(Numbers.compare);

	export const sortBigints = <T extends bigint>(self:ReadonlyArray<T>):Array<T> =>
		sortWith(self)(BigInts.compare);

	export const sortBooleans = <T extends boolean>(self:ReadonlyArray<T>):Array<T> =>
		sortWith(self)(Booleans.compare);

	export const sortDates = <T extends Date>(self:ReadonlyArray<T>):Array<T> =>
		sortWith(self)(Dates.compare);

	export const minimumWith	= <T>(self:ReadonlyArray<T>) => (comparator:Comparator<T>):Maybe<T>	=>
		self.length !== 0
		?	Maybe.some(self.reduce(Comparator.minimum(comparator)))
		:	Maybe.none();

	export const maximumWith	= <T>(self:ReadonlyArray<T>) => (comparator:Comparator<T>):Maybe<T>	=>
		self.length !== 0
		?	Maybe.some(self.reduce(Comparator.maximum(comparator)))
		:	Maybe.none();

	export const minimumOrNullWith	= <T>(self:ReadonlyArray<T>) => (comparator:Comparator<T>):T|null	=>
		Maybe.toNullable(minimumWith(self)(comparator));

	export const maximumOrNullWith	= <T>(self:ReadonlyArray<T>) => (comparator:Comparator<T>):T|null	=>
		Maybe.toNullable(maximumWith(self)(comparator));

	export const allMinimumWith	= <T>(self:ReadonlyArray<T>)	=> (comparator:Comparator<T>):Array<T>	=>
		allWith(self, comparator, -1);

	export const allMaximumWith	= <T>(self:ReadonlyArray<T>)	=> (comparator:Comparator<T>):Array<T>	=>
		allWith(self, comparator, +1);

	const allWith	= <T>(self:ReadonlyArray<T>, comparator:Comparator<T>, choice:1|-1):Array<T>	=> {
		let out:Array<T>	= [];
		for (const item of self) {
			if (out.length === 0) {
				out	= [ item ];
				continue;
			}
			const cmp	= comparator(item, out[0]);
			if (cmp === choice) {
				out	= [ item ];
			}
			else if (cmp === 0) {
				out.push(item);
			}
		}
		return out;
	};

	//-----------------------------------------------------------------------------
	//## traversal

	// TODO traverse add flatTraverse for everything everywhere

	// kleisli

	export const traverseKleisli	= <S>(self:ReadonlyArray<S>) => <C, T>(func:Fn<S, Fn<C, T>>):Fn<C, Array<T>>	=>
		(t:C) => self.map(item => func(item)(t));

	export const sequenceKleisli	= <C, T>(self:ReadonlyArray<Fn<C, T>>):Fn<C, Array<T>>	=>
		traverseKleisli(self)(Fn.identity);

	export const flatTraverseKleisli	= <S>(self:ReadonlyArray<S>) => <C, T>(func:Fn<S, Fn<C, ReadonlyArray<T>>>):Fn<C, Array<T>>	=>
		t => flatten(traverseKleisli(self)(func)(t));

	// nullable

	export const traverseNullable	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, T|null>):Array<T>|null	=> {
		const out	= [];
		for (const item of self) {
			const cand	= func(item);
			if (cand === null)	return null;
			out.push(cand);
		}
		return out;
	};

	export const sequenceNullable	= <T>(self:ReadonlyArray<T|null>):Array<T>|null	=>
		traverseNullable(self)(Fn.identity);

	export const flatTraverseNullable	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Nullable<ReadonlyArray<T>>>):Nullable<Array<T>>	=>
		Nullable.then(traverseNullable(self)(func))(flatten);

	// array

	// note this might not do as expected for ragged arrays
	export const traverseArray	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, ReadonlyArray<T>>):Array<Array<T>>	=> {
		const out:Array<Array<T>>	= [];
		for (const item of self) {
			const row	= func(item);
			for (let j=0; j<row.length; j++) {
				if (out.length <= j)	out.push([]);
				out[j].push(row[j]);
			}
		}
		return out;
	};

	export const sequenceArray	= <T>(self:ReadonlyArray<ReadonlyArray<T>>):Array<Array<T>>	=>
		traverseArray(self)(Fn.identity);

	export const flatTraverseArray	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, ReadonlyArray<ReadonlyArray<T>>>):Array<Array<T>>	=>
		traverseArray(self)(func).map(flatten);

	// maybe

	export const traverseMaybe	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Maybe<T>>):Maybe<Array<T>>	=> {
		const out:Array<T>	= [];
		for (const item of self) {
			const maybe	= func(item);
			if (Maybe.isNone(maybe))	return maybe;
			out.push(maybe.value);
		}
		return Maybe.some(out);
	};

	export const sequenceMaybe	= <T>(self:ReadonlyArray<Maybe<T>>):Maybe<Array<T>>	=>
		traverseMaybe(self)(Fn.identity);

	export const flatTraverseMaybe	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Maybe<ReadonlyArray<T>>>):Maybe<Array<T>>	=>
		Maybe.map(traverseMaybe(self)(func))(flatten);

	// either

	export const traverseEither	= <S>(self:ReadonlyArray<S>) => <E, T>(func:Fn<S, Either<E, T>>):Either<E, Array<T>>	=> {
		const values:Array<T>	= [];
		for (const item of self) {
			const cand	= func(item);
			if (Either.isLeft(cand))	return cand;
			values.push(cand.value);
		}
		return Either.right(values);
	};

	export const sequenceEither	= <E, T>(self:ReadonlyArray<Either<E, T>>):Either<E, Array<T>>	=>
		traverseEither(self)(Fn.identity);

	export const flatTraverseEither	= <S>(self:ReadonlyArray<S>) => <E, T>(func:Fn<S, Either<E, ReadonlyArray<T>>>):Either<E, Array<T>>	=>
		Either.map(traverseEither(self)(func))(flatten);

	// validated

	export const traverseValidated	= <S>(self:ReadonlyArray<S>) => <E, T>(func:Fn<S, Validated<E, T>>):Validated<E, Array<T>>	=> {
		const values:Array<T>	= [];
		const errors:Array<E>	= [];
		for (const item of self) {
			const validated	= func(item);
			if (Validated.isValid(validated))	values.push(validated.value);
			else								pushAll(errors)(validated.errors);
		}
		return Validated.unlessErrors(errors)(values);
	};

	export const sequenceValidated	= <E, T>(self:ReadonlyArray<Validated<E, T>>):Validated<E, Array<T>>	=>
		traverseValidated(self)(Fn.identity);

	export const flatTraverseValidated	= <S>(self:ReadonlyArray<S>) => <E, T>(func:Fn<S, Validated<E, ReadonlyArray<T>>>):Validated<E, Array<T>>	=>
		Validated.map(traverseValidated(self)(func))(flatten);

	// promise

	export const traversePromise	= <S>(self:ReadonlyArray<S>) => async <T>(func:Fn<S, Promise<T>>):Promise<Array<T>>	=> {
		const out	= [];
		for (const item of self) {
			out.push(await func(item));
		}
		return out;
	};

	export const sequencePromise	= <T>(self:ReadonlyArray<Promise<T>>):Promise<Array<T>>	=>
		traversePromise(self)(Fn.identity);

	export const flatTraversePromise	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Promise<ReadonlyArray<T>>>):Promise<Array<T>>	=>
		traversePromise(self)(func).then(flatten);

	// parallel promise

	export const parTraversePromise	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Promise<T>>):Promise<Array<T>>	=>
		Promise.all(self.map(func));

	export const parSequencePromise	= <T>(self:ReadonlyArray<Promise<T>>):Promise<Array<T>>	=>
		Promise.all(self);

	export const parFlatTraversePromise	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Promise<ReadonlyArray<T>>>):Promise<Array<T>>	=>
		parTraversePromise(self)(func).then(flatten);

	// sync

	export const traverseSync	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Sync<T>>):Sync<Array<T>>	=>
		() => self.map(it => func(it)());

	export const sequenceSync	= <S>(self:ReadonlyArray<Sync<S>>):Sync<Array<S>>	=>
		traverseSync(self)(Fn.identity);

	export const flatTraverseSync	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Sync<ReadonlyArray<T>>>):Sync<Array<T>>	=>
		() => self.flatMap(it => func(it)());

	// async

	export const traverseAsync	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Async<T>>):Async<Array<T>>	=>
		() => traversePromise(self)(it => func(it)());

	export const sequenceAsync	= <S>(self:ReadonlyArray<Async<S>>):Async<Array<S>>	=>
		traverseAsync(self)(Fn.identity);

	export const flatTraverseAsync	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Async<ReadonlyArray<T>>>):Async<Array<T>>	=>
		() => traverseAsync(self)(func)().then(flatten);

	// parallel async

	export const parTraverseAsync	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Async<T>>):Async<Array<T>>	=>
		() => parTraversePromise(self)(it => func(it)());

	export const parSequenceAsync	= <S>(self:ReadonlyArray<Async<S>>):Async<Array<S>>	=>
		parTraverseAsync(self)(Fn.identity);

	export const parFlatTraverseAsync	= <S>(self:ReadonlyArray<S>) => <T>(func:Fn<S, Async<ReadonlyArray<T>>>):Async<Array<T>>	=>
		() => parTraverseAsync(self)(func)().then(flatten);

	// zio

	export const traverseZio	= <S>(self:ReadonlyArray<S>) => <C, T>(func:Fn<S, Zio<C, T>>):Zio<C, Array<T>>	=>
		(c:C)	=> traversePromise(self)((it) => func(it)(c));

	export const sequenceZio	= <C, T>(self:ReadonlyArray<Zio<C, T>>):Zio<C, Array<T>>	=>
		traverseZio(self)(Fn.identity);

	export const flatTraverseZio	= <S>(self:ReadonlyArray<S>) => <C, T>(func:Fn<S, Zio<C, ReadonlyArray<T>>>):Zio<C, Array<T>>	=>
		(c:C)	=> traversePromise(self)((it) => func(it)(c)).then(flatten);

	// parallel zio

	export const parTraverseZio	= <S>(self:ReadonlyArray<S>) => <C, T>(func:Fn<S, Zio<C, T>>):Zio<C, Array<T>>	=>
		(c:C)	=> parTraversePromise(self)((it) => func(it)(c));

	export const parSequenceZio	= <C, T>(self:ReadonlyArray<Zio<C, T>>):Zio<C, Array<T>>	=>
		parTraverseZio(self)(Fn.identity);

	export const parFlatTraverseZio	= <S>(self:ReadonlyArray<S>) => <C, T>(func:Fn<S, Zio<C, ReadonlyArray<T>>>):Zio<C, Array<T>>	=>
		(c:C) => parTraversePromise(self)((it) => func(it)(c)).then(flatten);

	//-----------------------------------------------------------------------------

	// insert an item at specified index
	export const insertAtIndex	= <T>(self:ReadonlyArray<T>) => (index:number) => (item:T):Array<T>	=>
		[ ...self.slice(0, index), item, ...self.slice(index) ];

	// delete the item at the specified index
	export const removeAtIndex = <T>(self:ReadonlyArray<T>) => (index:number):Array<T> =>
		[ ...self.slice(0, index), ...self.slice(index + 1) ];

	// delete items matching a condition
	export const removeWhere = <T>(self:ReadonlyArray<T>) => (cond:Predicate<T>):Array<T> =>
		self.filter((it) => !cond(it));

	// replace the item at the given index with a new vaitemlue
	export const replaceAtIndex = <T>(self:ReadonlyArray<T>) => (index:number) => (item:T):Array<T> =>
		index < 0 || index > self.length ? [ ...self ] :
		[ ...self.slice(0, index), item, ...self.slice(index + 1) ];

	// replace items at the given indices with the given item
	export const replaceAtIndices = <T>(self:ReadonlyArray<T>) => (indices:ReadonlyArray<number>) => (item:T):Array<T>	=>
		indices.reduce((current, index) => replaceAtIndex(current)(index)(item), self as Array<T>);

	// replace all items matching a condition
	export const replaceWhere = <T>(self:ReadonlyArray<T>) => (cond:Predicate<T>) => (item:T):Array<T> =>
		self.map((it) => cond(it) ? item : it);

	// modify the item at the given index with a function
	export const modifyAtIndex = <T>(self:ReadonlyArray<T>) => (index:number) => (func:Endo<T>):Array<T> =>
		index < 0 || index >= self.length ? [ ...self ] :
		[ ...self.slice(0, index), func(self[index]), ...self.slice(index + 1) ];

	// modify items at the given indices with a function
	export const modifyAtIndices = <T>(self:ReadonlyArray<T>) => (indices:ReadonlyArray<number>) => (func:Endo<T>):Array<T>	=>
		indices.reduce((current, index) => modifyAtIndex(current)(index)(func), self as Array<T>);

	// modify all items matching a condition with a function
	export const modifyWhere = <T>(self:ReadonlyArray<T>) => (cond:Predicate<T>) => (func:Endo<T>):Array<T> =>
		self.map((it) => cond(it) ? func(it) : it);

	//------------------------------------------------------------------------------

	export const moveItem = <T>(self:ReadonlyArray<T>) => (fromIndex:number, toIndex:number):Array<T> => {
		const clone		= self.slice(0);
		const element	= clone[fromIndex];
		clone.splice(fromIndex, 1);
		clone.splice(toIndex, 0, element);
		return clone;
	};

	//------------------------------------------------------------------------------

	export const aggregate = <S, T>(self:ReadonlyArray<S>, equal:Equal<S>, build:(proto:S, group:Nea<S>)=>T):Array<T> => {
		const result	= [];
		let todo		= self;
		while (todo.length !== 0) {
			const first	= todo[0];
			const [ same, different ] = partition(todo)((it:S) => equal(first, it));
			const group	= build(first, Nea.unsafeFromArray(same));
			result.push(group);
			todo	= different;
		}
		return result;
	};

	export const aggregateByField	= <S, K extends keyof S, T>(self:ReadonlyArray<S>, key:K, build:(proto:S, group:Nea<S>)=>T):Array<T> =>
		aggregate<S, T>(self, (a, b) => a[key] === b[key], build);

	//------------------------------------------------------------------------------

	export const mappedSet	= <S>(self:ReadonlyArray<S>) => <T>(value:Fn<S, T>):Set<T>	=>
		new Set(self.map(value));

	// last value wins
	export const valueMap	= <T>(self:ReadonlyArray<T>) => <V>(value:Fn<T, V>):Map<T, V>	=>
		new Map(self.map(it => [ it, value(it) ]));

	export const histogram	= <T>(items:ReadonlyArray<T>) => <K>(discriminator:Fn<T, K>):ReadonlyMap<K, number>	=> {
		const out:Map<K, number>	= new Map();
		for (const item of items) {
			const key	= discriminator(item);
			const val	= out.get(key) ?? 0;
			out.set(key, val + 1);
		}
		return out;
	};

	//-----------------------------------------------------------------------------
	//## single maps for unique items

	/**
	index unique items by key
	same as singleMap(self)(Fn.identity)
	last key wins
	*/
	export const singleMapFromTuples	= <K, V>(self:ReadonlyArray<Tuple2<K, V>>):Map<K, V>	=>
		new Map(self);

	export const singleKeyMap	= <T>(self:ReadonlyArray<T>) => <K>(key:Fn<T, K>):Map<K, T>	=>
		singleMap(self)(it => [ key(it), it ]);

	/**
	index unique items by key
	same as singleMapFromTuples(self.map(entry))
	last key wins
	*/
	export const singleMap	= <T>(self:ReadonlyArray<T>) => <K, V>(entry:Fn<T, Tuple2<K, V>>):Map<K, V>	=>
		singleMapFilter(self)(entry);

	/** same as singleMap but removes items where the function returns null */
	export const singleMapFilter	= <T>(self:ReadonlyArray<T>) => <K, V>(entry:Fn<T, Tuple2<K, V>|null>):Map<K, V>	=> {
		const result	= new Map<K, V>();
		for (const item of self) {
			const maybe	= entry(item);
			if (maybe === null)	continue;

			const [ key, value ]	= maybe;
			result.set(key, value);
		}
		return result;
	};

	//-----------------------------------------------------------------------------
	//## multi maps for non-unique items

	/**
	index multiple values by key
	same as multiMap(self)(Fn.identity)
	*/
	export const multiMapFromTuples	= <K, V>(self:ReadonlyArray<Tuple2<K, V>>):Map<K, Nea<V>>	=>
		multiMap(self)(Fn.identity);

	export const multiKeyMap	= <T>(self:ReadonlyArray<T>) => <K>(key:Fn<T, K>):Map<K, Nea<T>>	=>
		multiMap(self)(it => [ key(it), it ]);

	/**
	index multiple items by key
	same as multiMapFromTuples(self.map(entry))
	*/
	export const multiMap	= <T>(self:ReadonlyArray<T>) => <K, V>(entry:Fn<T, Tuple2<K, V>>):Map<K, Nea<V>>	=>
		multiMapFilter(self)(entry);

	/** same as multiMap but removes items where the function returns null */
	export const multiMapFilter	= <T>(self:ReadonlyArray<T>) => <K, V>(entry:Fn<T, Tuple2<K, V>|null>):Map<K, Nea<V>>	=> {
		const result	= new Map<K, Nea.Mutable<V>>();
		for (const item of self) {
			const maybe	= entry(item);
			if (maybe === null)	continue;

			const [ key, value ]	= maybe;
			const old	= result.get(key);
			if (old === undefined)	result.set(key, [ value ]);
			else					old.push(value);
		}
		return result;
	};

	/** same as multiMap, but applies a function to each group */
	export const multiMapThen	= <T>(self:ReadonlyArray<T>) => <K, V>(entry:Fn<T, Tuple2<K, V>>) => <U>(build:Fn<Nea<V>, U>):Map<K, U>	=>
		Maps.mapValues(multiMap(self)(entry))(build);

	/** same as multiMapThen, but allows leaving out groups */
	export const multiMapThenCollapse	= <T>(self:ReadonlyArray<T>) => <K, V>(entry:Fn<T, Tuple2<K, V>>) => <U>(build:Fn<Nea<V>, Maybe<U>>):Map<K, U>	=>
		Maps.collapseMapValues(multiMap(self)(entry))(build);

	//------------------------------------------------------------------------------

	// split into chunks of a given size
	export const chunks	= <T>(self:ReadonlyArray<T>) => (size:number):Array<Array<T>>	=> {
		if (size <= 0)	throw new Error("chunk size must be at least 1");

		const out:Array<Array<T>>	= [];
		for (let i=0; i<self.length; i+=size) {
			out.push(self.slice(i, i+size));
		}
		return out;
	};

	export const splitAt = <T>(self:ReadonlyArray<T>) => (index:number):Tuple2<Array<T>, Array<T>> => [
		self.slice(0, index),
		self.slice(index),
	];

	//------------------------------------------------------------------------------

	// fisher-yates algorithm
	// attention: this is impure!
	export const randomShuffle	= <T>(self:ReadonlyArray<T>):Array<T>	=> {
		const result	= self.slice();

		let	todo	= self.length;
		while (todo > 0) {
			const index	= Numbers.randomInt(todo);
			todo--;

			const tmp		= result[todo];
			result[todo]	= result[index];
			result[index]	= tmp;
		}

		return result;
	};

	export const randomSelect	= <T>(items:ReadonlyArray<T>):T|undefined =>
		items[Numbers.randomInt(items.length)];

	//-----------------------------------------------------------------------------
	//## typeclasses

	export const equalOf	= <T>(base:Equal<T>):Equal<ReadonlyArray<T>>	=>
		(a, b) => equalWith(a, b)(base);

	export const equalOfBuiltin	= <T>():Equal<ReadonlyArray<T>>	=>
		equalOf(Equal.builtin);

	export const compareShorterFirst	= <T>(base:Comparator<T>):Comparator<ReadonlyArray<T>>	=>
		(as, bs) => {
			if (as === bs)	return 0;
			for (let i=0; i<as.length && i<bs.length; i++) {
				const tmp	= base(as[i], bs[i]);
				if (tmp !== 0)	return tmp;
			}
			return Numbers.compare(as.length, bs.length);
		};

	export const compareLongerFirst		= <T>(base:Comparator<T>):Comparator<ReadonlyArray<T>>	=>
		(as, bs) => {
			if (as === bs)	return 0;
			for (let i=0; i<as.length && i<bs.length; i++) {
				const tmp	= base(as[i], bs[i]);
				if (tmp !== 0)	return tmp;
			}
			return Numbers.compare(bs.length, as.length);
		};

	export const concatenatedMut	= <T>():Monoid<Array<T>>	=> ({
		empty:	[],
		concat:	concat,
	});

	export const concatenatedRo	= <T>():Monoid<ReadonlyArray<T>>	=> ({
		empty:	[],
		concat:	concat,
	});

	//-----------------------------------------------------------------------------
	//## mutation

	// just like array.push(...items) but without the stack overflow
	export const pushAll	= <T>(self:Array<T>) => (items:ReadonlyArray<T>):void	=> {
		for (const item of items)	self.push(item);
	};
}
